import {
  collection,
  query,
  where,
  getDocs,
  limit,
  Timestamp,
  type QuerySnapshot,
  type DocumentData,
} from 'firebase/firestore';

import { getFirestoreDb } from '../config/firebaseApp';
import type { UserProfile } from '../types/models';
import {
  babyBirthDateRangeForAgeMatch,
  referenceBirthTimestampForCompletedAgeMonths,
} from '../utils/babyAge';

const USERS = 'users';

/** Yakınımda: aynı il + ilçe */
export async function searchNearbyUsers(
  city: string,
  district: string,
  excludeUid: string,
  max = 50,
): Promise<UserProfile[]> {
  const db = getFirestoreDb();
  const q = query(
    collection(db, USERS),
    where('city', '==', city),
    where('district', '==', district),
    limit(max),
  );
  const snap = await getDocs(q);
  return mapDocsExcluding(snap, excludeUid);
}

/**
 * Ana sayfa: aynı doğum ayı (`babyBirthDate` bire bir eşit) — aynı ay/yaş kohortu.
 */
export async function searchSameBirthCohort(
  cohortBirth: Timestamp,
  excludeUid: string,
  max = 50,
): Promise<UserProfile[]> {
  const db = getFirestoreDb();
  const q = query(
    collection(db, USERS),
    where('babyBirthDate', '==', cohortBirth),
    limit(max),
  );
  const snap = await getDocs(q);
  return mapDocsExcluding(snap, excludeUid);
}

/** Genel arama: hedef tam ay sayısına göre ±marginMonths doğum bandı */
export async function searchByTargetBabyAgeMonths(
  targetAgeMonths: number,
  excludeUid: string,
  marginMonths = 1,
  max = 50,
): Promise<UserProfile[]> {
  const clamped = Math.max(0, Math.min(120, Math.floor(targetAgeMonths)));
  const ref = referenceBirthTimestampForCompletedAgeMonths(clamped);
  return searchBySimilarBabyAge(ref, excludeUid, marginMonths, max);
}

/** Referans doğum tarihine yakın (±marginMonths) kullanıcılar */
export async function searchBySimilarBabyAge(
  referenceBirth: Timestamp,
  excludeUid: string,
  marginMonths = 2,
  max = 50,
): Promise<UserProfile[]> {
  const { start, end } = babyBirthDateRangeForAgeMatch(referenceBirth, marginMonths);
  const db = getFirestoreDb();
  const startTs = Timestamp.fromDate(start);
  const endTs = Timestamp.fromDate(end);
  const q = query(
    collection(db, USERS),
    where('babyBirthDate', '>=', startTs),
    where('babyBirthDate', '<=', endTs),
    limit(max),
  );
  const snap = await getDocs(q);
  return mapDocsExcluding(snap, excludeUid);
}

function mapDocsExcluding(
  snap: QuerySnapshot<DocumentData>,
  excludeUid: string,
): UserProfile[] {
  const list: UserProfile[] = [];
  snap.forEach(d => {
    const u = d.data() as UserProfile;
    if (u.id !== excludeUid) {
      list.push(u);
    }
  });
  return list;
}
